import React, { FC, useEffect, useMemo, useState } from 'react'

import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { restrictToParentElement } from '@dnd-kit/modifiers'
import { SortableContext, arrayMove, rectSortingStrategy } from '@dnd-kit/sortable'
import { alpha, useTheme } from '@mui/material'
import Box from '@mui/material/Box'

import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { useThemeMode } from '../../../theme/ThemeModeContext'
import { changeBigWidgetsList } from '../DashboardSlice'
import { DrugStrip } from '../DrugStrip'
import { useGetBigWidgets } from '../hooks'
import { BigWidgetItem, BigWidgetsName } from '../types'

import { SortableWidget } from './SortableWidget'

export const LargeWidgetsSection: FC = () => {
  const { spacing, palette } = useTheme()
  const { resolvedMode } = useThemeMode()

  const dragHoverColor =
    resolvedMode === 'dark' ? alpha(palette.background.paper, 0.3) : palette.blue[400]

  const dispatch = useAppDispatch()

  const widgetsMap = useGetBigWidgets()
  const supportedWidgetIds = useMemo(
    () => Object.keys(widgetsMap) as BigWidgetsName[],
    [widgetsMap],
  )
  const { customize, bigWidgetsList } = useAppSelector(({ dashboard }) => dashboard)

  const initWidgetsOrder = Object.keys(bigWidgetsList)
    .filter(
      (item) =>
        bigWidgetsList[item as BigWidgetsName] &&
        supportedWidgetIds.includes(item as BigWidgetsName),
    )
    .map((widgetName) => ({
      id: widgetName,
    }))

  const [widgets, setWidgets] = useState<BigWidgetItem[]>(initWidgetsOrder as BigWidgetItem[])

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 150,
        tolerance: 8,
      },
    }),
  )

  const widgetIds = useMemo(() => widgets.map((widget) => widget.id), [widgets])

  const handleOrderChange = (updatedWidgets: BigWidgetItem[]) => {
    setWidgets(updatedWidgets)
    const widgetsNames = updatedWidgets.reduce((acc, bigWidget) => {
      acc[bigWidget.id] = bigWidgetsList[bigWidget.id]
      return acc
    }, {} as Record<keyof typeof BigWidgetsName, boolean>)
    supportedWidgetIds.forEach((item) => {
      if (!(item in widgetsNames)) {
        widgetsNames[item as BigWidgetsName] = bigWidgetsList[item as BigWidgetsName]
      }
    })

    dispatch(changeBigWidgetsList(widgetsNames))
  }

  const handleDragEnd = (event: DragEndEvent) => {
    if (!customize) {
      return
    }

    const { active, over } = event

    if (!over || active.id === over.id) {
      return
    }

    const oldIndex = widgets.findIndex((widget) => widget.id === active.id)
    const newIndex = widgets.findIndex((widget) => widget.id === over.id)

    if (oldIndex === -1 || newIndex === -1) {
      return
    }

    handleOrderChange(arrayMove(widgets, oldIndex, newIndex))
  }

  useEffect(() => {
    if (!customize) {
      const onlyVisible = Object.keys(bigWidgetsList)
        .filter(
          (item) =>
            bigWidgetsList[item as BigWidgetsName] &&
            supportedWidgetIds.includes(item as BigWidgetsName),
        )
        .map((widgetName) => ({
          id: widgetName,
        }))
      setWidgets(onlyVisible as BigWidgetItem[])
    } else {
      const fullList = supportedWidgetIds.map((widgetName) => ({
        id: widgetName,
      }))
      setWidgets(fullList as BigWidgetItem[])
    }
  }, [bigWidgetsList, customize, supportedWidgetIds])

  return (
    <Box
      sx={{
        padding: customize ? spacing(1) : 'unset',
        borderRadius: spacing(1),
        '&:hover': {
          backgroundColor: customize ? dragHoverColor : 'unset',
          transition: '0.4s all',
        },
      }}
    >
      {customize && <DrugStrip />}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToParentElement]}
      >
        <SortableContext items={widgetIds} strategy={rectSortingStrategy}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(auto-fit, minmax(${spacing(33.33)}, 1fr))`,
              gridAutoRows: '1fr',
              gap: spacing(2),
            }}
          >
            {widgets.map((widget) => (
              <SortableWidget key={widget.id} id={widget.id} disabled={!customize}>
                {widgetsMap[widget.id as keyof typeof widgetsMap]}
              </SortableWidget>
            ))}
          </Box>
        </SortableContext>
      </DndContext>
    </Box>
  )
}
